/**
 * `05/ Deployment` — the cross-CRF deployment register at
 * `/countries/:cc/deployment`.
 *
 * ===========================================================================
 * A REDEPLOY IS NOT A SECOND "DEPLOYED"
 * ===========================================================================
 * Every other view of a deployment lives INSIDE one work order, where the two
 * kinds are obvious because you can see the list. Flattened across a country they
 * are not, and a register that painted both the same word would hide the single
 * most useful fact on the screen: that this customer's software went out MORE
 * THAN ONCE. So `redeploy` carries its own label and its own tone pair, and the
 * one thing this screen refuses to do is render the two as one "Deployed".
 *
 * No colour is invented: `bg-ok-bg/text-ok-fg` is the pair `CrfsListPage` already
 * paints the `deployed` stage with, and `bg-warn-bg/text-warn-fg` is the amber
 * pair `STAGE_PILL_CLASSES` already carries. Amber here does NOT mean "bad" — a
 * redeploy is ordinary, often planned work. It means "look again", which is
 * exactly what a second delivery deserves.
 *
 * ===========================================================================
 * `?kind=` IS PASSED TO THE SERVER EXACTLY AS THE URL HOLDS IT
 * ===========================================================================
 * The server owns the vocabulary and answers 400 naming it. This screen does not
 * pre-filter the parameter: a hand-typed `?kind=bogus` that was silently dropped
 * would render the WHOLE register under a URL claiming to be filtered. The
 * server's own sentence is surfaced instead. See `deployment-api.ts`.
 *
 * ===========================================================================
 * `?crf=` IS NOT DRAWN, DELIBERATELY
 * ===========================================================================
 * The route accepts it and this screen sets it never — the design draws no
 * control for it, and a filter only a hand-typed URL can set is a filter nobody
 * can clear. The DL file register records the same decision for the same reason.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, EmptyState, SkeletonRows } from '../../components/ui';
import { relativeTime } from '../../domain/events';
import { formatDateLong } from '../inquiries/format';
import { cn } from '../../lib/cn';
import { deploymentKeys, fetchDeploymentRegister, registerErrorText } from './deployment-api';
import type { DeploymentRegisterFilters, DeploymentRegisterRow } from './deployment-api';

const PER_PAGE = 50;

/**
 * The filter chips, in the enum's own order with "All" in front. `value: null`
 * is the absence of the parameter, not a third kind.
 */
const KIND_FILTERS: readonly { value: string | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'deploy', label: 'Deploy' },
  { value: 'redeploy', label: 'Redeploy' },
];

/**
 * `Pill/Stage` geometry with existing tone pairs. Keyed by `string` rather than by
 * the union so an unrecognised kind renders as an honest neutral chip carrying the
 * SERVER'S OWN WORD — the `CrfStagePill` rule: never a blank pill, never a guessed
 * colour.
 */
const KIND_PILL: Readonly<Record<string, { label: string; classes: string }>> = {
  deploy: { label: 'Deploy', classes: 'bg-ok-bg text-ok-fg' },
  redeploy: { label: 'Redeploy', classes: 'bg-warn-bg text-warn-fg' },
};

/**
 * File-private, and `DeployedProjectsPage` keeps its own copy. The same house
 * pattern `PagerFooter` follows on every list screen: the tone pairs and the
 * "unknown kind keeps the server's word" rule are shared, the component is not.
 * That screen also renders the kind in a different shape — inline beside a date
 * rather than as a column of its own — so a shared component would have to grow a
 * variant prop to serve two callers.
 */
function kindPill(kind: string): { label: string; classes: string } {
  return KIND_PILL[kind] ?? { label: kind, classes: 'bg-subtle text-fg ring-1 ring-line' };
}

export function DeploymentRegisterPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();

  const canView = can(countryCode, 'crf.view');

  const rawKind = params.get('kind');
  const kind = rawKind === null || rawKind === '' ? null : rawKind;
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const filters = useMemo<DeploymentRegisterFilters>(
    () => ({ kind: kind ?? undefined, page, perPage: PER_PAGE }),
    [kind, page],
  );

  const list = useQuery({
    queryKey: deploymentKeys.deployments(countryCode, filters),
    queryFn: ({ signal }) => fetchDeploymentRegister(countryCode, filters, signal),
    enabled: canView,
  });

  if (!canView) {
    // D5 — the refusal is a designed state naming who to ask, and it never names
    // the permission key. Not a blank screen, and not a table rendered empty
    // (which would read as "nothing has been deployed").
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="Deployment is not available to this account">
          Viewing the deployment register in {countryCode} needs the CRF permission. Ask an
          administrator to widen your access.
        </Alert>
      </div>
    );
  }

  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? 0;

  const setKind = (next: string | null): void => {
    const patch = new URLSearchParams(params);
    if (next === null) patch.delete('kind');
    else patch.set('kind', next);
    // A filter change resets paging — page 3 of the redeploys is not page 3 of
    // the whole register.
    patch.delete('page');
    setParams(patch, { replace: true });
  };

  const goToPage = (next: number): void => {
    const patch = new URLSearchParams(params);
    if (next <= 1) patch.delete('page');
    else patch.set('page', String(next));
    setParams(patch);
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Deployment"
        subtitle={
          list.data === undefined
            ? undefined
            : // The SERVER'S total, never the length of this page.
              `${String(list.data.total)} ${list.data.total === 1 ? 'deployment' : 'deployments'}`
        }
      />

      <div role="group" aria-label="Filter by kind" className="flex flex-wrap items-center gap-2">
        {KIND_FILTERS.map((chip) => (
          <KindChip
            key={chip.label}
            label={chip.label}
            active={kind === chip.value}
            onSelect={() => setKind(chip.value)}
          />
        ))}
      </div>

      {list.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label="Loading the deployment register" />
        </div>
      ) : list.isError ? (
        <Alert tone="danger" title="The deployment register could not be loaded">
          {/* The server's own sentence — on a 400 it names the vocabulary. */}
          <p>{registerErrorText(list.error)}</p>
          <button
            type="button"
            onClick={() => {
              void list.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : rows.length === 0 && total > 0 ? (
        // An empty PAGE is not an empty RESULT SET — the quotes-list V3 lesson.
        <Alert tone="info" title={`Page ${String(list.data.page)} is past the end of this register`}>
          <p>
            The register holds {String(total)} {total === 1 ? 'deployment' : 'deployments'}.
          </p>
          <button
            type="button"
            onClick={() => goToPage(1)}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Show the first page.
          </button>
        </Alert>
      ) : rows.length === 0 ? (
        kind === null ? (
          <div className="rounded-md border border-dashed border-line bg-surface">
            <EmptyState title="No deployments have been recorded in this country yet." />
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-line bg-surface">
            <EmptyState
              title="No deployment in this country has that kind."
              action={
                <button
                  type="button"
                  onClick={() => setKind(null)}
                  className="text-body13 font-medium text-indigo hover:underline"
                >
                  Show every kind.
                </button>
              }
            />
          </div>
        )
      ) : (
        <DeploymentTable countryCode={countryCode} rows={rows} />
      )}

      {/* Every paging number below comes off the one payload the rows came off. */}
      {list.data !== undefined && rows.length > 0 ? (
        <PagerFooter
          page={list.data.page}
          perPage={list.data.perPage}
          shown={rows.length}
          total={list.data.total}
          onPage={goToPage}
        />
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── chips ── */

/** The Toolbar filter-chip geometry (6:467). No count: this route serves none. */
function KindChip({
  label,
  active,
  onSelect,
}: {
  label: string;
  active: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onSelect}
      className={cn(
        'flex h-8 items-center gap-1.5 rounded-sm border border-line bg-surface px-2.5 text-body13',
        active ? 'border-line-strong font-medium text-fg' : 'text-fg-secondary hover:text-fg',
      )}
    >
      {label}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────── table ── */

const HEADERS = ['CRF', 'CUSTOMER', 'KIND', 'MODE', 'DUE', 'DEPLOYED BY', 'NOTE'] as const;

/**
 * `deployments.mode` (migration 62). File-private, and the record panel keeps its
 * own copy — the `kindPill` house pattern: the rule is shared (NULL is an em-dash,
 * a stranger keeps the SERVER'S own word — never a blank, never a guessed label),
 * the component is not.
 */
const MODE_LABEL: Readonly<Record<string, string>> = { online: 'Online', offline: 'Offline' };
function modeLabel(mode: string | null): string {
  if (mode === null) return '—';
  return MODE_LABEL[mode] ?? mode;
}

function DeploymentTable({
  countryCode,
  rows,
}: {
  countryCode: string;
  rows: readonly DeploymentRegisterRow[];
}): React.JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[1320px] border-collapse text-left">
        <caption className="sr-only">Every deployment recorded in {countryCode}</caption>
        <colgroup>
          <col className="w-[170px]" />
          <col className="w-[170px]" />
          <col className="w-[120px]" />
          <col className="w-[100px]" />
          <col className="w-[110px]" />
          <col className="w-[150px]" />
          <col />
          <col className="w-[150px]" />
          <col className="w-[150px]" />
        </colgroup>

        <thead>
          <tr className="h-10 border-b border-line bg-subtle">
            {HEADERS.map((header) => (
              <th key={header} scope="col" className="px-3 text-th uppercase text-fg-muted">
                {header}
              </th>
            ))}
            {/*
             * Server-ordered by `deployed_at` — the moment the deployment HAPPENED,
             * not when the row was written. Stated, not a dead sort control.
             */}
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              DEPLOYED <span aria-hidden="true">↓</span>
              <span className="sr-only">, newest first</span>
            </th>
            <th scope="col" className="px-3">
              <span className="sr-only">Row actions</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => {
            const pill = kindPill(row.kind);
            return (
              <tr key={row.id} className="crf-register-row border-b border-line last:border-b-0 hover:bg-subtle">
                <td className="px-3 py-3 align-middle">
                  <Link
                    to={`/countries/${countryCode}/crfs/${row.crf_id}`}
                    className="font-mono text-mono text-fg underline underline-offset-2 hover:decoration-2"
                  >
                    {row.crf_doc_no}
                  </Link>
                </td>

                <td className="px-3 py-3 align-middle text-body13 text-fg">{row.party_name}</td>

                <td className="px-3 py-3 align-middle">
                  <span
                    // Addressed by KIND, never by row index: a test that asserts
                    // "the redeploy says redeploy" must not retarget if the order
                    // or the filter changes.
                    data-kind={row.kind}
                    className={cn(
                      'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-2.5 py-1 text-label font-medium',
                      pill.classes,
                    )}
                  >
                    <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
                    {pill.label}
                  </span>
                </td>

                {/* Migration 62: NULL is an em-dash in BOTH cells — never 'Online',
                  * never today's date. The back catalogue was never asked. */}
                <td
                  data-testid="deployment-mode"
                  className="px-3 py-3 align-middle text-body13 text-fg"
                >
                  {modeLabel(row.mode)}
                </td>

                <td
                  data-testid="deployment-due"
                  className="tnum px-3 py-3 align-middle text-body13 text-fg"
                >
                  {row.due_date ?? '—'}
                </td>

                <td className="px-3 py-3 align-middle text-body13 text-fg">
                  {row.deployed_by.name}
                </td>

                <NoteCell note={row.note} />

                <td className="px-3 py-3 align-middle">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-body13 text-fg">{formatDateLong(row.deployed_at)}</span>
                    <span className="text-label text-fg-muted">{relativeTime(row.deployed_at)}</span>
                  </div>
                </td>

                <td className="px-3 py-3 align-middle">
                  {/* The row's one action IS this link — a real anchor, so
                   * middle-click and copy-link-address work (the QuotesListPage
                   * precedent). It goes to the WORK ORDER, which is where the
                   * deployment was recorded and where a redeploy would be. */}
                  <Link
                    to={`/countries/${countryCode}/work/${row.work_order_id}`}
                    className="whitespace-nowrap text-body13 font-medium text-indigo hover:underline"
                  >
                    Open work order
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The deployment note.
 *
 * Unlike a failed test round's note this is CONTEXT, not a reason — nothing about
 * a deployment is a failure — so it is truncated into the column with the full
 * text in `title`, the same treatment a passing round's note gets. An absent note
 * is an em dash: `NO_VALUE`'s rule, never a blank cell.
 */
function NoteCell({ note }: { note: string | null }): React.JSX.Element {
  if (note === null || note.trim() === '') {
    return (
      <td className="px-3 py-3 align-middle">
        <span className="text-label text-fg-muted">—</span>
      </td>
    );
  }
  return (
    <td className="max-w-0 px-3 py-3 align-middle">
      <span title={note} className="block truncate text-body13 text-fg-secondary">
        {note}
      </span>
    </td>
  );
}

/* ────────────────────────────────────────────────────────────────── pager ── */

/**
 * `Table/Pagination` — re-authored per screen, the house pattern `CrfsListPage`,
 * `WorkConfirmationPage` and both Block B registers follow: the geometry is
 * shared, the component is file-private in each.
 */
function PagerFooter({
  page,
  perPage,
  shown,
  total,
  onPage,
}: {
  page: number;
  perPage: number;
  shown: number;
  total: number;
  onPage: (page: number) => void;
}): React.JSX.Element {
  const firstOnPage = total === 0 ? 0 : (page - 1) * perPage + 1;
  const lastOnPage = (page - 1) * perPage + shown;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface px-4 py-2">
      <p className="text-body13 text-fg-secondary">{perPage} per page</p>
      <div className="flex items-center gap-2">
        <p className="tnum text-body13 text-fg-secondary">
          {firstOnPage}–{lastOnPage} of {total}
        </p>
        <PageButton label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <ChevronLeft aria-hidden="true" className="size-4" />
        </PageButton>
        <PageButton
          label="Next page"
          disabled={lastOnPage >= total}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight aria-hidden="true" className="size-4" />
        </PageButton>
      </div>
    </div>
  );
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-7 items-center justify-center rounded-sm border border-line bg-surface text-fg-secondary hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
